import { computed } from "vue";
import { useModbusAddress } from "./useModbusAddress";
import { useModbusStore } from "@/stores/useModbusStore";
import { sensorSpecs } from "@/lib/sensors";
import { SensorCode } from "@/types/sensor/SensorCode";

// This is just in case the length is erroneously reported as 0
const DEFAULT_SENSOR_NAME_LENGTH = 1;

export function useSensorNames() {
  const { modbusAddress } = useModbusAddress();
  const modbusStore = useModbusStore();

  const getSensorName = (sensorCode: SensorCode): string => {
    let address: number | undefined;

    switch (sensorCode) {
      case "RS":
        address = modbusAddress.referenceSensorName;
        break;
      case "ISA":
        address = modbusAddress.sensorAName;
        break;
      case "ISB":
        address = modbusAddress.sensorBName;
        break;
      case "ISC":
        address = modbusAddress.sensorCName;
        break;
      case "DT":
        address = modbusAddress.deviceTemperatureName;
        break;
      default:
        // For sensors without name addresses (SP, IV, EV, IEV), always use hardcoded
        return sensorSpecs[sensorCode].name;
    }

    if (address === undefined) {
      return sensorSpecs[sensorCode].name;
    }

    // Try to get from hardware, fallback to hardcoded name
    const hardwareName: string | number | boolean | null =
      modbusStore.modbusAddrValue[String(address)];
    if (typeof hardwareName === "string" && hardwareName.trim() !== "") {
      return hardwareName.trim();
    }

    return sensorSpecs[sensorCode].name;
  };

  const sensorNames = computed(() => ({
    RS: getSensorName("RS"),
    ISA: getSensorName("ISA"),
    ISB: getSensorName("ISB"),
    ISC: getSensorName("ISC"),
    SP: getSensorName("SP"),
    IV: getSensorName("IV"),
    EV: getSensorName("EV"),
    IEV: getSensorName("IEV"),
    DT: getSensorName("DT"),
  }));

  function fetchSensorNames(deviceAddress: number) {
    const {
      referenceSensorName,
      sensorAName,
      sensorBName,
      sensorCName,
      deviceTemperatureName,
    } = modbusAddress;

    // Read sensor names if supported by this version
    if (
      referenceSensorName &&
      modbusStore.modbusAddrParams[referenceSensorName]
    ) {
      modbusStore.modbusRead(
        deviceAddress,
        referenceSensorName,
        modbusStore.modbusAddrLength[referenceSensorName] ||
          DEFAULT_SENSOR_NAME_LENGTH,
      );
    }
    if (sensorAName && modbusStore.modbusAddrParams[sensorAName]) {
      modbusStore.modbusRead(
        deviceAddress,
        sensorAName,
        modbusStore.modbusAddrLength[sensorAName] || DEFAULT_SENSOR_NAME_LENGTH,
      );
    }
    if (sensorBName && modbusStore.modbusAddrParams[sensorBName]) {
      modbusStore.modbusRead(
        deviceAddress,
        sensorBName,
        modbusStore.modbusAddrLength[sensorBName] || DEFAULT_SENSOR_NAME_LENGTH,
      );
    }
    if (sensorCName && modbusStore.modbusAddrParams[sensorCName]) {
      modbusStore.modbusRead(
        deviceAddress,
        sensorCName,
        modbusStore.modbusAddrLength[sensorCName] || DEFAULT_SENSOR_NAME_LENGTH,
      );
    }
    if (
      deviceTemperatureName &&
      modbusStore.modbusAddrParams[deviceTemperatureName]
    ) {
      modbusStore.modbusRead(
        deviceAddress,
        deviceTemperatureName,
        modbusStore.modbusAddrLength[deviceTemperatureName] ||
          DEFAULT_SENSOR_NAME_LENGTH,
      );
    }
  }

  return {
    getSensorName,
    sensorNames,
    fetchSensorNames,
  };
}
